/* eslint-disable no-undef */

import React, { useEffect, useState } from "react";

import axios from "axios";
import { getHeaders } from "../../../utility/getHeader";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ExportDataTable from "../../../components/Buttons/ExportdataTable";

import Header from '../../../components/Header';
import Layout from '../../../layout/Layout'
import { format } from "date-fns";
import DataTable from "../../../components/data-tables/dataTable";

import fetchData from "../../../functions/fetchData";
import './NewUser.css';

export default function NewUser() {
    let currentDate = format(new Date(), "yyyy-MM-dd");
    const userTypeOptions = [
        { value: "Admin", label: "Admin" },
        { value: "Cashier", label: "Cashier" },
        { value: "Account", label: "Account" },
        { value: "Manager", label: "Manager" },
        { value: "Gate Operation", label: "Gate Operation" },
    ];
    
    const [data, setData] = useState([]);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showConflictModal, setShowConflictModal] = useState(false);
    const [conflictDetails, setConflictDetails] = useState("");
    const [formdata, setFormData] = useState({
        name: "",
        pass: "",
        contact: "",
        email: "",
        type: "",
        lastloggedin: currentDate,
    });
    
    const columns = [
        { label: 'Name', field: 'name' },
        { label: 'Username', field: 'uname' },
        { label: 'Contact', field: 'contact' },
        { label: 'Email', field: 'email' },
        { label: 'Type', field: 'type' },
        { label: 'Last Logged In', field: 'last_loggedin' },
        { label: "Actions", field: "actions" }
    ];

    // Password strength calculation
    const getPasswordStrength = (password) => {
        if (!password) return { strength: 0, text: "", class: "" };
        
        let score = 0;
        if (password.length >= 8) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/[a-z]/.test(password)) score++;
        if (/[0-9]/.test(password)) score++;
        if (/[^A-Za-z0-9]/.test(password)) score++;

        const levels = [
            { strength: 0, text: "", class: "" },
            { strength: 1, text: "Very Weak", class: "strength-weak" },
            { strength: 2, text: "Weak", class: "strength-weak" },
            { strength: 3, text: "Fair", class: "strength-fair" },
            { strength: 4, text: "Good", class: "strength-good" },
            { strength: 5, text: "Strong", class: "strength-strong" }
        ];

        return levels[score];
    };

    const passwordStrength = getPasswordStrength(formdata.pass);

    // Form validation
    const validateForm = () => {
        const newErrors = {};

        if (!formdata.name?.trim()) newErrors.name = "Name is required";
        if (!formdata.pass) newErrors.pass = "Password is required";
        else if (formdata.pass.length < 6) newErrors.pass = "Password must be at least 6 characters";
        
        // Contact is optional, but if provided, validate format
        if (formdata.contact?.trim() && !/^\d{10,}$/.test(formdata.contact)) {
            newErrors.contact = "Contact must be at least 10 digits";
        }
        
        // Email is optional, but if provided, validate format
        if (formdata.email?.trim() && !/\S+@\S+\.\S+/.test(formdata.email)) {
            newErrors.email = "Email is invalid";
        }
        
        if (!formdata.type) newErrors.type = "User type is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const loadUsers = async () => {
        try {
            await fetchData('users', setData, 'id', {});
        } catch (error) {
            console.error('Error loading users:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error("Please fix the errors before submitting");
            return;
        }

        setLoading(true);

        try {
            await axios.post(
                "/register",
                {
                    name: formdata.name,
                    pass: formdata.pass,
                    contact: formdata.contact,
                    email: formdata.email,
                    type: formdata.type,
                    lastloggedin: currentDate,
                },
                getHeaders()
            );

            // Fetch the updated data after successful submission
            await loadUsers();

            toast.success('User added successfully!');
            setFormData({
                name: "",
                pass: "",
                contact: "",
                email: "",
                type: "",
                lastloggedin: currentDate,
            });
            setErrors({});
        } catch (err) {
            console.error('Error in adding user:', err);
            
            if (err.response?.status === 409) {
                // Handle conflict error
                const errorMessage = err.response?.data?.message || 'User already exists';
                const conflictField = err.response?.data?.field || 'user';
                
                setConflictDetails(`A user with this ${conflictField} already exists.`);
                setShowConflictModal(true);
                
                // Highlight the conflicting field
                if (conflictField === 'email' || errorMessage.includes('email')) {
                    setErrors(prev => ({ ...prev, email: "This email is already registered" }));
                } else if (conflictField === 'contact' || errorMessage.includes('contact')) {
                    setErrors(prev => ({ ...prev, contact: "This contact number is already registered" }));
                } else if (conflictField === 'name' || errorMessage.includes('name')) {
                    setErrors(prev => ({ ...prev, name: "This username is already taken" }));
                } else {
                    setErrors(prev => ({ ...prev, general: errorMessage }));
                }
                
                toast.error(`Registration failed: ${errorMessage}`);
            } else {
                toast.error('Error in adding user. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const closeConflictModal = () => {
        setShowConflictModal(false);
        setConflictDetails("");
    };

    useEffect(() => {
        const fetchAndSetData = async () => {
            try {
                await fetchData('users', setData, 'id', {});
            } catch (error) {
                console.error('Error in useEffect:', error);
            }
        };

        fetchAndSetData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    return (
        <>
            <Layout>
                <Header title="User Management" />
                <ToastContainer 
                    position="top-right"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                />
                
                <div className="new-user-container">
                    <div className="row">
                        {/* Enhanced User Registration Form */}
                        <div className="col-12">
                            <div className="user-form-card">
                                <div className="form-header">
                                    <h2 className="form-title">Add New User</h2>
                                    <p className="form-subtitle">Create a new user account with secure credentials</p>
                                </div>

                                <form onSubmit={handleSubmit}>
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label className="form-label">Full Name *</label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formdata.name || ""}
                                                onChange={handleInputChange}
                                                className={`enhanced-input ${errors.name ? 'error' : ''}`}
                                                placeholder="Enter full name"
                                            />
                                            {errors.name && (
                                                <div className="error-message">
                                                    <span className="error-icon">⚠️</span>
                                                    {errors.name}
                                                </div>
                                            )}
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Contact Number</label>
                                            <input
                                                type="tel"
                                                name="contact"
                                                value={formdata.contact || ""}
                                                onChange={handleInputChange}
                                                className={`enhanced-input ${errors.contact ? 'error' : ''}`}
                                                placeholder="Enter contact number (optional)"
                                            />
                                            {errors.contact && (
                                                <div className="error-message">
                                                    <span className="error-icon">⚠️</span>
                                                    {errors.contact}
                                                </div>
                                            )}
                                        </div>

                                        <div className="form-group full-width">
                                            <label className="form-label">Email Address</label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formdata.email || ""}
                                                onChange={handleInputChange}
                                                className={`enhanced-input ${errors.email ? 'error' : ''}`}
                                                placeholder="Enter email address (optional)"
                                            />
                                            {errors.email && (
                                                <div className="error-message">
                                                    <span className="error-icon">⚠️</span>
                                                    {errors.email}
                                                </div>
                                            )}
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Password *</label>
                                            <input
                                                type="password"
                                                name="pass"
                                                value={formdata.pass || ""}
                                                onChange={handleInputChange}
                                                className={`enhanced-input ${errors.pass ? 'error' : ''}`}
                                                placeholder="Enter secure password"
                                            />
                                            {formdata.pass && passwordStrength.strength > 0 && (
                                                <div className="password-strength">
                                                    <div className="strength-bar">
                                                        <div className={`strength-fill ${passwordStrength.class}`}></div>
                                                    </div>
                                                    <div className={`strength-text ${passwordStrength.class}`}>
                                                        {passwordStrength.text}
                                                    </div>
                                                </div>
                                            )}
                                            {errors.pass && (
                                                <div className="error-message">
                                                    <span className="error-icon">⚠️</span>
                                                    {errors.pass}
                                                </div>
                                            )}
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">User Type *</label>
                                            <select
                                                name="type"
                                                value={formdata.type || ""}
                                                onChange={handleInputChange}
                                                className={`enhanced-input ${errors.type ? 'error' : ''}`}
                                            >
                                                <option value="">Select User Type</option>
                                                {userTypeOptions.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.type && (
                                                <div className="error-message">
                                                    <span className="error-icon">⚠️</span>
                                                    {errors.type}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {errors.general && (
                                        <div className="error-message" style={{marginBottom: '20px'}}>
                                            <span className="error-icon">⚠️</span>
                                            {errors.general}
                                        </div>
                                    )}

                                    <div className="submit-section">
                                        <button
                                            type="submit"
                                            className="enhanced-submit-btn"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <div className="loading-spinner"></div>
                                                    Creating User...
                                                </>
                                            ) : (
                                                <>
                                                    <span>💾</span>
                                                    Create User
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        {/* Enhanced Users Table */}
                        <div className="col-12">
                            <div className="users-table-card">
                                <div className="table-header">
                                    <h3 className="table-title">Registered Users</h3>
                                    <ExportDataTable
                                        tableId="usersTable"
                                        tableData={data}
                                    />
                                </div>
                                
                                {data.length === 0 ? (
                                    <div style={{textAlign: 'center', padding: '40px', color: '#7f8c8d'}}>
                                        <div style={{fontSize: '3rem', marginBottom: '20px'}}>👥</div>
                                        <h4>No Users Found</h4>
                                        <p>Create the first user account to get started</p>
                                    </div>
                                ) : (
                                    <div className="table-scroll-container" style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
                                        <DataTable 
                                            columns={columns} 
                                            data={data} 
                                            tablename="users"
                                            id="usersTable"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Conflict Resolution Modal */}
                {showConflictModal && (
                    <div className="conflict-modal">
                        <div className="modal-content">
                            <div className="modal-icon">⚠️</div>
                            <h3 className="modal-title">User Already Exists</h3>
                            <p className="modal-message">{conflictDetails}</p>
                            <div className="modal-actions">
                                <button 
                                    className="modal-btn secondary"
                                    onClick={closeConflictModal}
                                >
                                    Close
                                </button>
                                <button 
                                    className="modal-btn primary"
                                    onClick={() => {
                                        closeConflictModal();
                                        // Clear form or navigate to edit
                                        setFormData({
                                            name: "",
                                            pass: "",
                                            contact: "",
                                            email: "",
                                            type: "",
                                            lastloggedin: currentDate,
                                        });
                                    }}
                                >
                                    Create New User
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </Layout>
        </>
    )
}
